import { useEffect, useState } from 'react';

export type OfferCountdown = {
  /** True when the offer deadline has passed (or none was provided). */
  isExpired: boolean;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

const getRemainingSeconds = (expiresAtSeconds: number): number =>
  Math.max(0, Math.floor(expiresAtSeconds - Date.now() / 1000));

/**
 * Seconds-granularity countdown to an offer deadline (`offer.expires_at`,
 * unix seconds). Recomputes from the wall clock on every tick so it can't
 * drift, and stops ticking once expired.
 */
export const useOfferCountdown = (
  expiresAtSeconds: number | undefined
): OfferCountdown => {
  const [remaining, setRemaining] = useState(() =>
    expiresAtSeconds === undefined ? 0 : getRemainingSeconds(expiresAtSeconds)
  );

  useEffect(() => {
    if (expiresAtSeconds === undefined) {
      setRemaining(0);
      return () => {};
    }

    setRemaining(getRemainingSeconds(expiresAtSeconds));
    const interval = setInterval(() => {
      const next = getRemainingSeconds(expiresAtSeconds);
      setRemaining(next);
      if (next <= 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [expiresAtSeconds]);

  return {
    isExpired: remaining <= 0,
    days: Math.floor(remaining / 86_400),
    hours: Math.floor((remaining % 86_400) / 3600),
    minutes: Math.floor((remaining % 3600) / 60),
    seconds: remaining % 60,
  };
};
